import { useEffect, useState } from 'react';
import { adminApi, type Member } from './api/adminClient';
import StatusBadge from './components/StatusBadge';
import InviteOperatorModal from './components/InviteOperatorModal';
import CompSubModal from './components/CompSubModal';
import MemberDetailDrawer from './components/MemberDetailDrawer';

export default function AdminUsersPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [role, setRole] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState<{ page: number; total: number; members: Member[] } | null>(null);
  const [drawer, setDrawer] = useState<string | null>(null);
  const [invite, setInvite] = useState(false);
  const [comp, setComp] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    if (status) params.set('status', status);
    if (role) params.set('role', role);
    if (search) params.set('search', search);
    adminApi
      .listMembers(params)
      .then((d) => {
        setData(d);
        setErr(null);
      })
      .catch((e: Error) => setErr(e.message));
  };

  useEffect(load, [page, status, role, search]);

  const counts =
    data?.members.reduce(
      (acc, m) => ({ ...acc, [m.status]: (acc[m.status] ?? 0) + 1 }),
      {} as Record<string, number>,
    ) ?? {};

  const togglePause = async (m: Member) => {
    const next = m.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    await adminApi.patchMember(m._id, {
      status: next,
      paused_reason: next === 'PAUSED' ? 'manual_admin' : undefined,
    });
    load();
  };

  const flipRole = async (m: Member) => {
    const next = m.role === 'OPERATOR' ? 'USER' : 'OPERATOR';
    await adminApi.setRole(m._id, next);
    load();
  };

  const checkout = async (m: Member, plan: 'STARTER' | 'PRO' | 'PREMIUM') => {
    try {
      const { url } = await adminApi.checkoutLink(m._id, plan);
      await navigator.clipboard.writeText(url);
      alert(`URL de checkout copiada al portapapeles:\n${url}`);
    } catch (e) {
      alert(`Error generando checkout: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Usuarios</h1>
        <button className="btn btn-primary btn-sm" onClick={() => setInvite(true)}>
          + Invitar operador
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="stat bg-base-100 rounded-box shadow">
          <div className="stat-title">Activos</div>
          <div className="stat-value text-2xl text-success">{counts.ACTIVE ?? 0}</div>
        </div>
        <div className="stat bg-base-100 rounded-box shadow">
          <div className="stat-title">Pausados</div>
          <div className="stat-value text-2xl text-warning">{counts.PAUSED ?? 0}</div>
        </div>
        <div className="stat bg-base-100 rounded-box shadow">
          <div className="stat-title">Desconectados</div>
          <div className="stat-value text-2xl text-error">{counts.DISCONNECTED ?? 0}</div>
        </div>
      </div>

      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <div className="flex gap-2 mb-4 flex-wrap">
            <input
              className="input input-bordered input-sm flex-1 min-w-[220px]"
              placeholder="Buscar por correo o nombre"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
            />
            <select
              className="select select-bordered select-sm"
              value={status}
              onChange={(e) => {
                setPage(1);
                setStatus(e.target.value);
              }}
            >
              <option value="">Estado</option>
              <option value="ACTIVE">Activo</option>
              <option value="PAUSED">Pausado</option>
              <option value="DISCONNECTED">Desconectado</option>
            </select>
            <select
              className="select select-bordered select-sm"
              value={role}
              onChange={(e) => {
                setPage(1);
                setRole(e.target.value);
              }}
            >
              <option value="">Rol</option>
              <option value="USER">Usuario</option>
              <option value="OPERATOR">Operador</option>
            </select>
          </div>

          {err && <div className="alert alert-error mb-2">{err}</div>}

          <div className="overflow-x-auto">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Correo</th>
                  <th>Estado</th>
                  <th>Rol</th>
                  <th>Plan</th>
                  <th>Visa</th>
                  <th>Hoy</th>
                  <th>Total</th>
                  <th>Replies</th>
                  <th>Limit</th>
                  <th>Last sent</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(data?.members ?? []).map((m) => (
                  <tr key={m._id} className="hover">
                    <td className="link" onClick={() => setDrawer(m._id)}>
                      {m.email}
                    </td>
                    <td>
                      <StatusBadge value={m.status} />
                      {m.paused_reason && (
                        <span className="text-xs opacity-60 ml-1">({m.paused_reason})</span>
                      )}
                    </td>
                    <td>
                      <StatusBadge value={m.role} />
                    </td>
                    <td>{m.plan ?? '—'}</td>
                    <td>
                      {!m.visa_types?.length ? (
                        <span className="badge badge-ghost badge-sm">Ambas</span>
                      ) : (
                        m.visa_types.map((v) => (
                          <span key={v} className="badge badge-info badge-sm mr-1">
                            {v === 'h2b' ? 'H2B' : 'H2A'}
                          </span>
                        ))
                      )}
                    </td>
                    <td>{m.sent_today}</td>
                    <td>{m.sent_total}</td>
                    <td>{m.replied}</td>
                    <td>{m.daily_limit}</td>
                    <td className="text-xs">
                      {m.last_sent_at ? new Date(m.last_sent_at).toLocaleString() : '—'}
                    </td>
                    <td>
                      <div className="dropdown dropdown-end">
                        <button className="btn btn-ghost btn-xs">⋯</button>
                        <ul className="dropdown-content menu bg-base-100 rounded-box shadow z-10 w-56">
                          <li>
                            <a onClick={() => togglePause(m)}>
                              {m.status === 'ACTIVE' ? 'Pausar' : 'Reanudar'}
                            </a>
                          </li>
                          <li>
                            <a onClick={() => flipRole(m)}>
                              {m.role === 'OPERATOR' ? 'Quitar operador' : 'Promover a operador'}
                            </a>
                          </li>
                          <li>
                            <a onClick={() => setComp(m._id)}>Comp suscripción</a>
                          </li>
                          <li className="menu-title">
                            <span>Generar checkout</span>
                          </li>
                          <li>
                            <a onClick={() => checkout(m, 'STARTER')}>STARTER</a>
                          </li>
                          <li>
                            <a onClick={() => checkout(m, 'PRO')}>PRO</a>
                          </li>
                          <li>
                            <a onClick={() => checkout(m, 'PREMIUM')}>PREMIUM</a>
                          </li>
                          <li>
                            <a onClick={() => setDrawer(m._id)}>Ver detalle</a>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                ))}
                {data && data.members.length === 0 && (
                  <tr>
                    <td colSpan={11} className="text-center opacity-60 py-8">
                      Sin resultados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="join mt-4">
            <button
              className="join-item btn btn-sm"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              «
            </button>
            <button className="join-item btn btn-sm">Página {data?.page ?? 1}</button>
            <button
              className="join-item btn btn-sm"
              disabled={!data || page * 25 >= data.total}
              onClick={() => setPage(page + 1)}
            >
              »
            </button>
          </div>
        </div>
      </div>

      {drawer && <MemberDetailDrawer memberId={drawer} onClose={() => setDrawer(null)} />}
      {invite && (
        <InviteOperatorModal
          onClose={() => setInvite(false)}
          onCreated={() => {
            setInvite(false);
            load();
          }}
        />
      )}
      {comp && (
        <CompSubModal
          memberId={comp}
          onClose={() => setComp(null)}
          onDone={() => {
            setComp(null);
            load();
          }}
        />
      )}
    </div>
  );
}
